<template>
    <Card
        :size="`col-md-10 col-lg-8 col-xl-6 max-xl-900 mt-4`"
        :newLayout="true"
        :hideFooter="true"
    >
        <template slot="card-header">
            <h1 v-translate>Subscriptions</h1>
        </template>
        <template slot="card-body">
            <div class="subscription-tab subscription-standalone text-white px-3">
                <div class="widget-subscriptions subscriptions subscriptions--portal position-relative">
                    <div
                        class="subscriptions__rail subscriptions__rail--portal w-100"
                        :class="{'notinteractive': anyProductsMatch}"
                        ref="matchScroll"
                    >
                        <div
                            class="subscription subscription--portal subscription--active"
                            :class="{'notinteractive': subscription.Id != selectedUpgrade.combiProductId}"
                            v-for="(subscription, ind) in subscriptions"
                            :key="`portal-loc-${ind}`"
                        >
                            <portal-target
                                :name="`portal-${ind}`"
                                slim
                            >
                            </portal-target>
                        </div>
                    </div>
                    <div
                        v-if="!subscriptions.length"
                        class="text-center"
                    >
                        <p v-translate>You currently have no available subscriptions to manage.</p>
                    </div>
                    <div
                        class="subscriptions__rail"
                        ref="subscriptionRail"
                        v-else
                    >
                        <div
                            class="subscription position-relative"
                            v-for="(subscription, ind) in subscriptions"
                            :key="ind"
                        >
                            <portal :to="`portal-${ind}`">
                                <SubscriptionInner
                                    :subscriptions="subscriptions"
                                    :subscription="subscription"
                                    :upgradeSubscription="upgradeSubscription"
                                    :userIsSubscribedTo="userIsSubscribedTo"
                                    :isPendingCancellation="isPendingCancellation"
                                    :paymentFrequency="paymentFrequency"
                                    :selectedUpgrade="selectedUpgrade"
                                    :initVideoModal="initVideoModal"
                                    :pricingOptionChanged="pricingOptionChanged"
                                    :portal="true"
                                    :closeBox="closeBox"
                                />
                            </portal>
                            <SubscriptionInner
                                :subscriptions="subscriptions"
                                :subscription="subscription"
                                :upgradeSubscription="upgradeSubscription"
                                :userIsSubscribedTo="userIsSubscribedTo"
                                :isPendingCancellation="isPendingCancellation"
                                :paymentFrequency="paymentFrequency"
                                :selectedUpgrade="selectedUpgrade"
                                :initVideoModal="initVideoModal"
                                :pricingOptionChanged="pricingOptionChanged"
                                :portal="false"
                                :closeBox="closeBox"
                            />
                        </div>
                    </div>
                </div>
                <div class="payment-history">
                    <div class="row align-items-center mt-4 mb-2">
                        <div class="col-6">
                            <h3 v-translate>Payment History</h3>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table payment-history__table">
                            <thead>
                                <tr>
                                    <th v-translate>Date</th>
                                    <th v-translate>Amount</th>
                                    <th v-translate>Subscription</th>
                                    <th v-translate>Status</th>
                                    <th
                                        style="text-align: center;"
                                        v-translate
                                    >Invoice</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(payment, index) in paymentAttempts"
                                    :key="index"
                                >
                                    <td>{{ payment.date }}</td>
                                    <td>{{ payment.amount }} {{ payment.currency }}</td>
                                    <td>{{ payment.subscription }}</td>
                                    <td :class="{ 'status-failed': !payment.isSuccessful }">{{ (payment.isSuccessful ? 'Paid' : 'Failed') | translate }}</td>
                                    <td>
                                        <a
                                            style="display: block; text-align: center;"
                                            :href="'/invoice/' + payment.invoice"
                                            :alt="'Download invoice' | translate"
                                            v-if="payment.isSuccessful"
                                        >
                                            <svg-rndr
                                                svg="download-file.svg"
                                                :inline="true"
                                                :width="18"
                                            />
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <modal
                name="subscription-video"
                width="100%"
                height="auto"
                v-show="subscriptions.length"
            >
                <VimeoPlayer
                    :videoId="videoId"
                    v-if="videoId"
                />
                <span
                    class="modal-close"
                    @click="hideModal"
                >
                    <svg-rndr svg="close.svg" />
                </span>
            </modal>
        </template>
    </Card>
</template>

<script>
import { debounce } from 'throttle-debounce';
import PerfectScrollbar from 'perfect-scrollbar';
import { mapActions, mapState, mapMutations } from 'vuex';
import { mapFields } from 'vuex-map-fields';

import { paymentFrequencyForPurchaseOption } from '@H/enrolment';
import { isDesktop, isMobile } from '@H/device_helpers';
import { addClass } from '@H/class-list';
import Card from '@C/Card';
import VimeoPlayer from '@C/VimeoPlayer';
import SubscriptionInner from '../Widgets/SubscriptionInner';
import route from '@H/route';

export default {
    components: { VimeoPlayer, SubscriptionInner, Card },
    beforeDestroy() {
        if (isDesktop()) {
            this.destroyScrollbar();

            if (this.$refs.subscriptionRail) {
                this.$refs.subscriptionRail.removeEventListener('ps-scroll-x', this.scrollMatchHandler);
            }
        } else {
            this.$refs.subscriptionRail.removeEventListener('scroll', this.scrollMatchHandler);
        }

        if (this.$refs.matchScroll) {
            this.$refs.matchScroll.removeEventListener('scroll', this.scrollLockHandler);
        }
    },
    mounted() {
        if (isDesktop()) {
            this.initScroll();
        } else {
            this.$nextTick(() => {
                this.$refs.subscriptionRail.addEventListener('scroll', this.scrollMatchHandler);
                addClass(this.$refs.subscriptionRail, 'overflowx-auto');
            });
        }
        this.$nextTick(() => {
            this.$refs.matchScroll.addEventListener('scroll', this.scrollLockHandler);
        });
    },
    data() {
        return {
            activeDialog: null,
            videoId: null,
            scrollbar: null,
            scrollLeft: 0,
            noAvatar: '/img/profile-default.svg',

            activeSubscriptions: [],
        };
    },
    watch: {
        scrollbar(bar) {
            if (bar) {
                this.scrollLeft = this.$refs.subscriptionRail.scrollLeft;

                this.$refs.subscriptionRail.addEventListener('ps-scroll-x', this.scrollMatchHandler);

                window.addEventListener(
                    'resize',
                    this.scrollResizeHandler
                );
            }
        },
        subscriptions(subscriptions) {
            if (subscriptions.length) {
                this.activeSubscriptions = subscriptions.filter(subscription => subscription.subscriptionStatus == 'Subscribed');
            }
        }
    },
    methods: {
        ...mapMutations('Widget', ['setSelectedUpgrade']),
        initScroll() {
            this.$nextTick(() => {
                let subRailRef = this.$refs.subscriptionRail;
                if (!subRailRef) return;

                this.scrollbar = new PerfectScrollbar(subRailRef, {
                    suppressScrollY: true,
                    wheelPropagation: true
                });
                // Scroll width incorrect at load time so triggering
                // the resize handler to verify whether we need the
                // scroll bar or not.
                this.scrollResizeHandler();
            });
        },
        destroyScrollbar() {
            if (this.scrollbar) {
                this.scrollbar.destroy();
                this.scrollbar = null;
                window.removeEventListener('resize', this.scrollResizeHandler);
            }
        },
        scrollResizeHandler: debounce(100, function () {
            let subRailRef = this.$refs.subscriptionRail;
            if (subRailRef.offsetWidth < subRailRef.scrollWidth) {
                if (this.scrollbar) {
                    this.scrollbar.update();
                } else {
                    this.initScroll();
                }
            } else {
                if (this.scrollbar) {
                    this.scrollbar.destroy();
                    this.scrollbar = null;
                }
            }
        }),
        scrollMatchHandler(e) {
            this.scrollLeft = e.target.scrollLeft;
            this.$refs.matchScroll.scrollLeft = this.scrollLeft;
        },
        scrollLockHandler(e) {
            if (e.type == 'scroll') {
                e.preventDefault();

                this.$refs.subscriptionRail.scrollLeft = e.target.scrollLeft;
                this.scrollLeft = e.target.scrollLeft;
            }
            return false;
        },
        closeBox() {
            this.selectedUpgrade.combiProductId = null;
            if (isDesktop()) {
                this.initScroll();
            }
        },
        paymentFrequency(pricingOptionId) {
            let subscription = this.subscriptions.find(sub => sub.subscriptionId == this.selectedUpgrade.subscriptionId),
                purchaseOption = subscription.pricing.find(pricing => pricing.Id == pricingOptionId),
                months = purchaseOption.Purchase_Option_Number__c;

            return paymentFrequencyForPurchaseOption(months);
        },
        pricingOptionChanged(e) {
            let target = e.target || e;

            let pricingOption = {
                id: target.value,
                price: target.options[target.selectedIndex].getAttribute('data-price')
            };
            this.selectedUpgrade = { ...this.selectedUpgrade, pricingOption };
        },

        /**
         * Either puts the subscription into an active state on the UI, or if already active, loads up the billing address screen
         */
        upgradeSubscription(e, subscription) {

            if (this.selectedUpgrade.combiProductId == null || this.selectedUpgrade.combiProductId != subscription.Id) {
                if (!isMobile()) {
                    this.scrollToCard(e.target);
                }
            }
            this.$nextTick(() => {
                this.destroyScrollbar();

                // First time clicking upgrade on a product, we want to put the subscription into an active state on the UI
                if (this.selectedUpgrade.combiProductId == null || this.selectedUpgrade.combiProductId != subscription.Id) {
                    this.selectedUpgrade = {
                        subscriptionId: subscription.subscriptionId,
                        combiProductId: subscription.Id,
                        pricingOption: {
                            id: subscription.pricing[0].Id,
                            price: subscription.pricing[0].Price__c
                        },
                        message: subscription.message
                    };
                    return this.selectedUpgrade;
                }

                if (subscription.subscriptionStatus == 'Teaser') {
                    this.$router.push({ name: 'my-subscriptions-teaser' });
                } else {
                    this.$router.push({ name: 'my-subscriptions-billing' });
                }
            });
        },
        scrollToCard(button) {
            const card = button.closest('.subscription');
            const rails = document.querySelectorAll('.subscriptions__rail');
            [...rails].map(rail => rail.scroll({ left: card.offsetLeft }));
        },
        userIsSubscribedTo(subscription) {
            if (
                typeof this.user.subscription == 'undefined' ||
                !this.user.subscription.length
            )
                return false;

            // Pluck a list of product ids ['a000000001', 'a0000310fs0'] etc.
            let userProducts = this.user.subscription.map(subscription => subscription.Id);

            return userProducts.includes(subscription.Id);
        },
        isPendingCancellation(subscription) {
            if (
                typeof this.user.subscription == 'undefined' ||
                !this.user.subscription.length
            )
                return false;

            let activeSubIds = this.activeSubscriptions.map(subscription => subscription.Id);

            return activeSubIds.includes(subscription.Id) && subscription.status == 'Pending Cancellation';
        },
        initVideoModal(e) {
            e.preventDefault();
            let hrefSplit = e.target.href.split('/');
            let videoId = hrefSplit[hrefSplit.length - 1];
            this.videoId = videoId;
            this.showModal();
        },
        showModal() {
            this.$modal.show('subscription-video');
        },
        hideModal() {
            this.$modal.hide('subscription-video');
        },
    },
    filters: {
        formatDescription(value) {
            if (!value) return value;

            return value
                .split('\n')
                .map(bullet => {
                    return '<li>' + bullet + '</li>';
                })
                .join('');
        }
    },
    computed: {
        /**
         * Format the users active subs into a string like: "sub name 1, sub name 2 and sub name 3"
         * With the subscription names wrapped in <strong> tags
         */
        subscribedTo() {
            if (typeof this.user.subscription == 'undefined') return;

            let userSubscriptions = this.user.subscription.slice(0),
                lastInList = userSubscriptions.pop(),
                commaSeparatedNames = userSubscriptions.map(subscription => subscription.Name).join(', ');

            let subscribedToString = `<strong>${commaSeparatedNames}</strong>`;

            if (typeof lastInList != 'undefined') {
                subscribedToString += ` and <strong>${lastInList.Name}</strong>`;
            }

            return subscribedToString;
        },
        anyProductsMatch() {
            let match = true;
            for (let i = 0; i < this.subscriptions.length; i++) {
                if (this.subscriptions[i].Id == this.selectedUpgrade.combiProductId) {
                    match = false;
                }
            }
            return match;
        },
        ...mapFields('Widget', ['selectedUpgrade']),
        ...mapState('Widget', ['paymentAttempts', 'user', 'subscriptions']),
        ...mapState('Enrolment', ['user']),
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/stages/MySubscriptions/Index.vue